import React from 'react';
import styled from 'styled-components';
import { IoMdAdd, IoMdClose } from 'react-icons/io';
import { HorizontalDiv, InputDate } from '../Common';
import colors from './../../res/styles/Colors';
import dimension from './../../res/styles/Responsive';
import { getDateOrEmpty } from './../../utils/DateFormatter';
import LinePoint from './LinePoint';

/**
 * Component for managing multiple dates for a single field (like Pinching or Harvesting)
 * Allows adding/removing multiple date entries
 */
export default function MultiDateField({
    className,
    dates = [], // Array of date timestamps
    content,
    hasLine = true,
    color = 'black',
    isEditable = false,
    id,
    onChange,
    onAddDate,
    onRemoveDate
}) {
    // When not editable, sort and show only non-null dates chronologically
    // When editable, keep original order (including nulls) to maintain index mapping
    let displayDates;
    let indexMapping = [];

    if (isEditable) {
        // Keep original order, show at least one entry
        displayDates = dates.length === 0 ? [null] : dates;
        indexMapping = displayDates.map((_, idx) => idx); // Direct mapping
    } else {
        // Sort chronologically, filter out nulls
        const withIndices = dates
            .map((date, originalIndex) => ({ date, originalIndex }))
            .filter(item => item.date != null)
            .sort((a, b) => a.date - b.date);

        displayDates = withIndices.map(item => item.date);
        indexMapping = withIndices.map(item => item.originalIndex);

        // If no dates, show at least one empty entry so the field is visible
        if (displayDates.length === 0) {
            displayDates = [null];
            indexMapping = [0];
        }
    }

    // Check for duplicates
    const getDuplicateDates = () => {
        if (!isEditable) return new Set();
        const validDates = dates.filter(d => d != null);
        const duplicates = new Set();
        const seen = new Set();
        validDates.forEach(date => {
            if (seen.has(date)) {
                duplicates.add(date);
            }
            seen.add(date);
        });
        return duplicates;
    };

    const duplicateDates = getDuplicateDates();

    return (
        <Container className={className}>
            {displayDates.map((date, displayIndex) => {
                const originalIndex = indexMapping[displayIndex];
                const hasMultipleDates = displayDates.filter(d => d != null).length > 1;
                const isDuplicate = date != null && duplicateDates.has(date);

                return (
                    <StyledHorizontalDiv key={`${id}-${originalIndex}`}>
                        <DateColumn>
                            {isEditable ? (
                                <StyledInputDate
                                    id={`${id}-${originalIndex}`}
                                    color={color}
                                    dateAsLong={date}
                                    $isDuplicate={isDuplicate}
                                    onChange={(event) => {
                                        onChange && onChange(id, originalIndex, event.target.value);
                                    }}
                                />
                            ) : (
                                <StyledDate>{getDateOrEmpty(date, 'DD/MMM/YYYY')}</StyledDate>
                            )}
                            {isDuplicate && (
                                <DuplicateWarning>Duplicate date!</DuplicateWarning>
                            )}
                            {isEditable && (
                                <ButtonsContainer>
                                    {displayIndex === displayDates.length - 1 && (
                                        <AddButton
                                            onClick={() => onAddDate && onAddDate(id)}
                                            title='Add another date'
                                            color={color}
                                            disabled={date == null}
                                        >
                                            <IoMdAdd />
                                        </AddButton>
                                    )}
                                    <RemoveButton
                                        onClick={() => onRemoveDate && onRemoveDate(id, originalIndex)}
                                        title='Remove this date'
                                        disabled={date == null}
                                    >
                                        <IoMdClose />
                                    </RemoveButton>
                                </ButtonsContainer>
                            )}
                        </DateColumn>
                        <StyledLinePoint
                            color={color}
                            hasLine={displayIndex > 0 || hasLine}
                            circlePosition='top'
                        />
                        <ContentText>
                            {content}
                            {hasMultipleDates && ` (${displayIndex + 1})`}
                        </ContentText>
                    </StyledHorizontalDiv>
                );
            })}
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledHorizontalDiv = styled(HorizontalDiv)`
    p {
        font-size: 13px;
        margin-bottom: 1rem;
    }
`;

const DateColumn = styled.div`
    flex-basis: 80px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const StyledDate = styled.p`
    color: ${colors.darker_gray};
    margin-bottom: 0.3rem;
`;

const StyledLinePoint = styled(LinePoint)`
    flex-shrink: 0;
    width: 30px;
    height: ${({ hasLine }) => (hasLine ? 'auto' : 'fit-content')};
`;

const StyledInputDate = styled(InputDate)`
    && {
        font-size: 12px;
        margin-bottom: 0.5rem;
        border: ${({ $isDuplicate }) => $isDuplicate ? '2px solid #ff0000' : '1px solid #ced4da'};
        background-color: ${({ $isDuplicate }) => $isDuplicate ? '#fff5f5' : 'white'};
        
        @media ${dimension.md} {
            width: 150px;
            height: 30px;
        }

        :focus {
            box-shadow: none;
            border-color: ${({ $isDuplicate, color }) => $isDuplicate ? '#ff0000' : color};
        }
    }
`;

const DuplicateWarning = styled.span`
    color: ${colors.red};
    font-size: 11px;
    font-weight: bold;
    margin-bottom: 0.3rem;
`;

const ContentText = styled.p`
    font-weight: bold;
    margin: 0;
`;

const ButtonsContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    margin-top: 0;
    margin-bottom: 16px;
`;

const AddButton = styled.button`
    background-color: ${({ color }) => color || colors.green};
    color: white;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    max-width: 28px;
    max-height: 28px;
    cursor: pointer;
    padding: 0;
    margin: 0;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

    svg {
        width: 20px;
        height: 20px;
    }

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
    }

    &:active {
        transform: scale(0.95);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        transform: none;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
`;

const RemoveButton = styled.button`
    background-color: ${colors.red};
    color: white;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    max-width: 28px;
    max-height: 28px;
    cursor: pointer;
    padding: 0;
    margin: 0;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

    svg {
        width: 20px;
        height: 20px;
    }

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
    }

    &:active {
        transform: scale(0.95);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        transform: none;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
`;

